import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { OptimizedImage } from '../components/ui/OptimizedImage';

const Brands = () => {
  const [activeBrand, setActiveBrand] = useState(0);

  // Datos de las sub-marcas de Jarad actualizados con el sistema de diseño
  const brands = [
    {
      id: 1,
      name: 'Jarad Vital',
      tagline: 'Nutrición esencial para tu mascota',
      description: 'Nuestra línea premium de alimentos que proporciona todos los nutrientes esenciales para mantener a tu mascota saludable y vital. Formulada con ingredientes de alta calidad y vitaminas esenciales.',
      color: '#f6cf02', // Brand yellow
      image: '/brands/Logo Jarad Pet Food.png',
      features: ['Alta en proteínas', 'Rica en vitaminas', 'Sin conservantes artificiales', 'Adecuada para todas las edades']
    },
    {
      id: 2,
      name: 'Jarad Plus',
      tagline: 'Nutrición avanzada para el bienestar',
      description: 'Nuestra línea especializada que combina ingredientes naturales con suplementos avanzados para proporcionar una nutrición óptima. Ideal para mascotas con necesidades específicas.',
      color: '#8ed1fc', // Birds category color
      image: '/brands/jarad-plus.png',
      features: ['Ingredientes naturales', 'Suplementos avanzados', 'Fórmula equilibrada', 'Para necesidades específicas']
    },
    {
      id: 3,
      name: 'Jarad Active',
      tagline: 'Energía y vitalidad para mascotas activas',
      description: 'Diseñada para mascotas con alto nivel de actividad física. Proporciona la energía necesaria para mantener a tu mascota activa y en forma, con un equilibrio perfecto de nutrientes.',
      color: '#e8b4a0', // Dogs category color
      image: '/brands/Logo Jarad Active +Menú .png',
      features: ['Alto contenido energético', 'Proteínas de calidad', 'Para mascotas activas', 'Ayuda al desarrollo muscular']
    },
    {
      id: 4,
      name: 'PREMIfood',
      tagline: 'Nutrición 100% natural y premium',
      description: 'Nuestra línea más pura, elaborada exclusivamente con ingredientes naturales y premium. Sin aditivos artificiales, perfecta para mascotas con sensibilidades alimentarias.',
      color: '#c7c7c7', // Cats category color
      image: '/brands/Logo Jarad PREMIfood Diet Complet.png',
      features: ['100% natural', 'Ingredientes premium', 'Sin aditivos artificiales', 'Para sensibilidades alimentarias']
    }
  ];

  useEffect(() => {
    document.title = "Nuestras Marcas | Jarad - Nutrición Premium para Mascotas";
  }, []);

  return (
    <>
      <Helmet>
        <title>Nuestras Marcas | Jarad</title>
        <meta name="description" content="Descubre las líneas de productos Jarad: Vital, Plus, Active y PREMIfood. Nutrición de calidad para tu mascota." />
      </Helmet>

      <div style={{backgroundColor: '#f2ede4'}} className="min-h-screen">
        {/* Hero Section */}
        <div className="relative h-[70vh] overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10"></div>
            <OptimizedImage 
              src="/brands/hero-bg.webp" 
              alt="Jarad Brands" 
              width={1920}
              height={1080}
              type="banner"
              className="w-full h-full object-cover"
              lazy={false}
            />
          </div>
          
          <div className="relative z-20 h-full flex flex-col justify-center items-center text-white px-4 pt-16">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-extrabold text-center mb-4 tracking-tight"
              style={{color: 'white'}}
            >
              Nuestras Marcas
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl text-center max-w-3xl mb-8 font-light"
              style={{color: 'white'}}
            >
              Descubre nuestra gama completa de productos diseñados para el bienestar de tu mascota
            </motion.p>
          </div>
        </div>

        {/* Navegación de marcas */}
        <div className="sticky top-16 z-10 shadow-md bg-[#f2ede4]">
          <div className="content-container">
            <div className="flex flex-wrap justify-center py-4">
              {brands.map((brand, index) => (
                <motion.button
                  key={brand.id}
                  onClick={() => setActiveBrand(index)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-6 py-3 mx-2 mb-2 rounded-full transition-all duration-300 font-medium ${
                    activeBrand === index 
                      ? 'text-white shadow-lg' 
                      : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                  style={{
                    backgroundColor: activeBrand === index ? brand.color : undefined,
                    color: activeBrand === index ? '#0d0d0d' : '#6B7280'
                  }}
                >
                  {brand.name}
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        {/* Contenido de la marca activa */}
        <div className="py-16">
          <div className="content-container">
            <AnimatePresence mode="wait">
              <motion.div 
                key={activeBrand}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
              >
                {/* Imagen de la marca */}
                <motion.div 
                  className="relative h-80 lg:h-96 rounded-2xl overflow-hidden shadow-xl flex items-center justify-center"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <OptimizedImage 
                    src={brands[activeBrand].image} 
                    alt={brands[activeBrand].name} 
                    width={300}
                    height={160}
                    type="product"
                    className="h-40 object-contain"
                  />
                  <div 
                    className="absolute inset-0 opacity-10"
                    style={{backgroundColor: brands[activeBrand].color}}
                  ></div>
                </motion.div>

                {/* Información de la marca */}
                <div className="flex flex-col">
                  <motion.h2 
                    className="text-3xl font-bold mb-3"
                    style={{color: '#0d0d0d'}}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    {brands[activeBrand].name}
                  </motion.h2>
                  <motion.p 
                    className="text-xl mb-4"
                    style={{color: '#6B7280'}}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    {brands[activeBrand].tagline}
                  </motion.p>
                  <motion.p 
                    className="text-gray-700 mb-8"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    {brands[activeBrand].description}
                  </motion.p>
                  
                  <motion.div 
                    className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    {brands[activeBrand].features.map((feature, index) => (
                      <motion.div 
                        key={index} 
                        className="flex items-center"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.6 + index * 0.1 }}
                      >
                        <div 
                          className="w-2 h-2 rounded-full mr-3"
                          style={{backgroundColor: brands[activeBrand].color}}
                        ></div>
                        <span className="text-gray-700">{feature}</span>
                      </motion.div>
                    ))}
                  </motion.div>
                  
                  <motion.button 
                    className="px-6 py-3 rounded-full font-medium shadow-md hover:shadow-lg transition-all w-fit border-2"
                    style={{
                      backgroundColor: '#f6cf02',
                      color: '#0d0d0d',
                      borderColor: '#0d0d0d'
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#0d0d0d';
                      e.currentTarget.style.color = '#f6cf02';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f6cf02';
                      e.currentTarget.style.color = '#0d0d0d';
                    }}
                  >
                    Ver productos
                  </motion.button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Sección de valores */}
        <div className="py-16">
          <div className="content-container">
            <motion.h2 
              className="text-3xl lg:text-4xl font-bold text-center mb-12"
              style={{color: '#0d0d0d'}}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              Nuestros Valores
            </motion.h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  title: 'Calidad',
                  description: 'Utilizamos ingredientes de la más alta calidad en todos nuestros productos.',
                  icon: (
                    <svg className="w-10 h-10" style={{color: '#f6cf02'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"></path>
                    </svg>
                  )
                },
                {
                  title: 'Innovación',
                  description: 'Constantemente investigamos y desarrollamos nuevas fórmulas para mejorar la nutrición de tu mascota.',
                  icon: (
                    <svg className="w-10 h-10" style={{color: '#8ed1fc'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                    </svg>
                  )
                },
                {
                  title: 'Sostenibilidad',
                  description: 'Nos comprometemos con prácticas sostenibles y respetuosas con el medio ambiente.',
                  icon: (
                    <svg className="w-10 h-10" style={{color: '#e8b4a0'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path>
                    </svg>
                  )
                },
                {
                  title: 'Compromiso',
                  description: 'Dedicamos nuestro trabajo a mejorar la calidad de vida de las mascotas y sus familias.',
                  icon: (
                    <svg className="w-10 h-10" style={{color: '#c7c7c7'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                    </svg>
                  )
                }
              ].map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10, transition: { duration: 0.2 } }}
                  className="p-6 rounded-2xl shadow-md text-center hoverable-card"
                >
                  <div className="flex justify-center mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{color: '#0d0d0d'}}>{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-16 px-4 overflow-hidden" style={{backgroundColor: '#0d0d0d'}}>
          <div className="content-container text-center relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold mb-6 text-white">
                ¿Listo para descubrir nuestros productos?
              </h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90 font-light">
                Explora nuestra gama completa de productos Jarad y encuentra la nutrición perfecta para tu mascota.
              </p>
              <motion.button 
                className="px-8 py-3 rounded-full font-medium shadow-md hover:shadow-lg transition-all border-2"
                style={{
                  backgroundColor: '#f6cf02',
                  color: '#0d0d0d',
                  borderColor: '#f6cf02'
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#f6cf02';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f6cf02';
                  e.currentTarget.style.color = '#0d0d0d';
                }}
              >
                Ver todos los productos
              </motion.button>
            </motion.div>
            
            {/* Elementos decorativos animados */}
            <motion.div 
              className="absolute top-0 right-0 w-32 h-32 rounded-full"
              style={{backgroundColor: '#f6cf02', opacity: 0.1}}
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.1, 0.2, 0.1]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.div 
              className="absolute bottom-0 left-0 w-24 h-24 rounded-full"
              style={{backgroundColor: '#f6cf02', opacity: 0.1}}
              animate={{ 
                scale: [1, 1.3, 1],
                opacity: [0.1, 0.15, 0.1]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Brands;